import React, { useRef, useEffect, useCallback } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { Html } from '@react-three/drei';
import * as THREE from 'three';
import { ROOM_W, ROOM_H, API } from './constants';
import { playApproachNote } from './audio';

const FRAME_STYLES = {
  'the-fire':          { outer: '#1C0E06', rough: 0.42, metal: 0.38, accent: '#B8860B', mat: null,      innerGap: 0 },
  'souffle-de-vie':    { outer: '#3D1A08', rough: 0.32, metal: 0.68, accent: '#DAA520', mat: null,      innerGap: 0 },
  'un-echange-ardent': { outer: '#4A2E14', rough: 0.78, metal: 0.08, accent: '#C9A84C', mat: '#F4EFE7', innerGap: 0.08 },
  'lenvoy':            { outer: '#1A1614', rough: 0.55, metal: 0.24, accent: '#C8A855', mat: '#F0F2F5', innerGap: 0.08 },
  'la-benediction':    { outer: '#2C1A0C', rough: 0.70, metal: 0.10, accent: '#C9A84C', mat: '#F5F1EB', innerGap: 0.06 },
  'feu-pierre':        { outer: '#221C16', rough: 0.85, metal: 0.06, accent: '#A89060', mat: null,      innerGap: 0 },
  'entre-harpe-epee':  { outer: '#100A04', rough: 0.55, metal: 0.30, accent: '#C4905A', mat: null,      innerGap: 0 },
  'already-done':      { outer: '#1E1208', rough: 0.42, metal: 0.45, accent: '#C07840', mat: null,      innerGap: 0 },
  // Nouveaux cadres
  'le-veilleur':       { outer: '#0D0B1A', rough: 0.42, metal: 0.28, accent: '#9BA8B8', mat: '#F0F3F5', innerGap: 0.07 },
  'sous-la-presence':  { outer: '#2A1606', rough: 0.52, metal: 0.38, accent: '#D4A860', mat: null,      innerGap: 0 },
  'rabbi-shimon':      { outer: '#080C1C', rough: 0.44, metal: 0.40, accent: '#C9A84C', mat: null,      innerGap: 0 },
};
const DEFAULT_STYLE = { outer: '#2A1E10', rough: 0.65, metal: 0.45, accent: '#C9A84C', mat: '#F5F2EC', innerGap: 0.06 };

// ── Hiérarchie lumineuse — variation naturelle par œuvre ──────────────────────────────────
// Hero : œuvres dramatiques ou centrales → lumière plus présente
// Soft : petits formats ou compositions douces → lumière plus retenue
// Default : galerie standard
const ARTWORK_INTENSITY = {
  'the-fire':          { base: 92, near: 145 }, // hero — rouge dramatique
  'souffle-de-vie':    { base: 92, near: 145 }, // hero — or lumineux
  'rabbi-shimon':      { base: 96, near: 150 }, // hero — fond sombre, besoin fort
  'sous-la-presence':  { base: 86, near: 132 }, // horizontal large — éclairage doux étendu
  'le-veilleur':       { base: 72, near: 112 }, // petit portrait nocturne — douceur
  'lenvoy':            { base: 82, near: 128 }, // standard légèrement retenu
};

// ── Cache global pour éviter de recharger les textures ──
const _textureCache = new Map();
const _sharedLoader = new THREE.TextureLoader();

// Préchargement optimisé : proches en premier, reste en arrière-plan
if (typeof window !== 'undefined') {
  import('../../data/artworks').then(({ ARTWORKS }) => {
    const START_Z = 24; // position de départ du joueur
    const sorted = [...ARTWORKS].sort((a, b) =>
      Math.abs(a.z - START_Z) - Math.abs(b.z - START_Z)
    );

    const loadOne = (aw) => {
      if (!aw.imageUrl || _textureCache.has(aw.imageUrl)) return;
      _sharedLoader.load(
        aw.imageUrl,
        (tex) => {
          tex.colorSpace = THREE.SRGBColorSpace;
          tex.generateMipmaps = true;
          tex.needsUpdate = true;
          _textureCache.set(aw.imageUrl, tex);
        },
        undefined,
        () => {}
      );
    };

    // Les 5 premières œuvres chargées immédiatement (visibles dès le départ)
    sorted.slice(0, 5).forEach(loadOne);

    // Le reste après 300ms : toutes chargées avant que le visiteur atteigne la moitié de la galerie
    setTimeout(() => sorted.slice(5).forEach(loadOne), 300);
  });
}

function ArtworkTexturedMaterial({ artwork, isNear }) {
  const matRef = useRef();
  const texRef = useRef(null);
  const { gl } = useThree();
  const maxAniso = gl.capabilities.getMaxAnisotropy();

  useEffect(() => {
    if (!artwork.imageUrl || !matRef.current) return;

    const applyTex = (tex) => {
      if (!matRef.current) return;
      tex.colorSpace      = THREE.SRGBColorSpace;
      tex.minFilter       = THREE.LinearMipmapLinearFilter;
      tex.magFilter       = THREE.LinearFilter;
      tex.generateMipmaps = true;
      tex.anisotropy      = Math.min(maxAniso, 8);
      tex.needsUpdate     = true;
      texRef.current             = tex;
      matRef.current.map         = tex;
      matRef.current.color.setHex(0xffffff);
      matRef.current.toneMapped  = false;
      matRef.current.needsUpdate = true;
    };

    if (_textureCache.has(artwork.imageUrl)) {
      applyTex(_textureCache.get(artwork.imageUrl));
      return;
    }

    let cancelled = false;
    _sharedLoader.load(
      artwork.imageUrl,
      (tex) => {
        if (cancelled) return;
        _textureCache.set(artwork.imageUrl, tex);
        applyTex(tex);
      },
      undefined,
      (err) => { if (!cancelled) console.warn('Texture error:', artwork.id, err); }
    );
    return () => { cancelled = true; };
  }, [artwork.imageUrl, artwork.id, gl, maxAniso]);

  // Boost texture quality when viewer is standing on the viewing circle
  useEffect(() => {
    const tex = texRef.current;
    if (!tex || !matRef.current) return;
    tex.anisotropy         = isNear ? maxAniso : Math.min(maxAniso, 8);
    tex.minFilter          = isNear ? THREE.LinearFilter : THREE.LinearMipmapLinearFilter;
    tex.needsUpdate        = true;
    matRef.current.needsUpdate = true;
  }, [isNear, maxAniso]);

  return <meshBasicMaterial ref={matRef} color="#ffffff" toneMapped={false} />;
}

export default function ArtworkFrame({ artwork, onSelect, onHover, isHovered, isNear, isMobile }) {
  const isLeft    = artwork.wall === 'left';
  const wallX     = isLeft ? -(ROOM_W / 2) + 0.055 : (ROOM_W / 2) - 0.055;
  const rotation  = isLeft ? [0, Math.PI / 2, 0] : [0, -Math.PI / 2, 0];
  const lightX    = isLeft ? -(ROOM_W / 2) + 1.6 : (ROOM_W / 2) - 1.6;
  const worldPos  = [wallX, artwork.yPos || 5.0, artwork.z];

  const lightColor = artwork.z > 2 ? '#FFF2DC' : artwork.z > -12 ? '#FFF8F0' : '#FFFCF8';
  const coneColor  = artwork.z > 2 ? '#FFE0A0' : artwork.z > -12 ? '#FFE8A0' : '#FFF0CC';

  const primaryLightRef = useRef();
  const fillLightRef    = useRef();
  const pulseLineRef    = useRef();
  const prevIsNear      = useRef(false);

  const intMap = ARTWORK_INTENSITY[artwork.id] ?? { base: 65, near: 102 };

  useEffect(() => {
    if (isNear && !prevIsNear.current) playApproachNote();
    prevIsNear.current = isNear;
  }, [isNear]);

  useFrame(({ clock }, delta) => {
    const speed = delta * 2.5;
    const targetPrimary = isNear ? intMap.near : intMap.base;
    const targetFill    = isNear ? (isMobile ? 12 : 18) : (isMobile ? 6 : 10);
    if (primaryLightRef.current) {
      const diff = targetPrimary - primaryLightRef.current.intensity;
      if (Math.abs(diff) > 0.15) primaryLightRef.current.intensity += diff * Math.min(speed, 0.9);
      else primaryLightRef.current.intensity = targetPrimary;
    }
    if (fillLightRef.current) {
      const diff = targetFill - fillLightRef.current.intensity;
      if (Math.abs(diff) > 0.06) fillLightRef.current.intensity += diff * Math.min(speed, 0.9);
      else fillLightRef.current.intensity = targetFill;
    }
    // Pulsing gold line — 3s cycle, ease in/out
    // Near: 0.7 → 1.0  |  Far: subtle 0.18 → 0.30 (design element)
    if (pulseLineRef.current) {
      const t     = clock.getElapsedTime();
      const phase = (Math.sin(t * (2 * Math.PI / 3)) + 1) / 2; // 0–1, 3s period
      const target = isNear
        ? 0.70 + 0.30 * phase
        : 0.18 + 0.12 * phase;
      pulseLineRef.current.material.opacity = target;
    }
  });

  const handleClick = useCallback((e) => {
    e.stopPropagation();
    onSelect(artwork);
    fetch(`${API}/api/artwork/click`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ artwork_id: artwork.id }),
    }).catch(() => {});
  }, [artwork, onSelect]);

  const handleOver = useCallback((e) => { e.stopPropagation(); document.body.style.cursor = 'pointer'; onHover(artwork.id); }, [artwork.id, onHover]);
  const handleOut  = useCallback((e) => { e.stopPropagation(); document.body.style.cursor = 'default'; onHover(null); }, [onHover]);

  const style   = FRAME_STYLES[artwork.id] || DEFAULT_STYLE;
  const borderW = artwork.width  + 0.22;
  const borderH = artwork.height + 0.22;
  const accentW = artwork.width  + 0.075;
  const accentH = artwork.height + 0.075;

  return (
    <group>
      <group position={worldPos} rotation={rotation}>
        <mesh position={[0, 0, -0.026]} renderOrder={1}>
          <boxGeometry args={[borderW, borderH, 0.055]} />
          <meshStandardMaterial color={style.outer} roughness={style.rough} metalness={style.metal} envMapIntensity={0.9} />
        </mesh>
        <mesh position={[0, 0, -0.005]} renderOrder={2}>
          <boxGeometry args={[accentW, accentH, 0.032]} />
          <meshStandardMaterial color={style.accent} roughness={0.18} metalness={0.94} emissive={style.accent} emissiveIntensity={0.20} />
        </mesh>
        {style.mat && (
          <mesh position={[0, 0, 0.013]} renderOrder={3}>
            <planeGeometry args={[artwork.width + style.innerGap, artwork.height + style.innerGap]} />
            <meshBasicMaterial color={style.mat} />
          </mesh>
        )}
        <mesh position={[0, 0, 0.016]} renderOrder={4}
          onClick={handleClick} onPointerOver={handleOver} onPointerOut={handleOut}
          data-testid={`artwork-${artwork.id}`}
        >
          <planeGeometry args={[artwork.width, artwork.height]} />
          <ArtworkTexturedMaterial artwork={artwork} isNear={isNear} />
        </mesh>
        {isHovered && (
          <mesh position={[0, 0, 0.024]} renderOrder={5}>
            <planeGeometry args={[artwork.width + 0.01, artwork.height + 0.01]} />
            <meshBasicMaterial color={style.accent} transparent opacity={0.07} depthWrite={false} />
          </mesh>
        )}

        {/* ── Ligne or pulsée — 12px de gap sous le bord inférieur du cadre ── */}
        <mesh ref={pulseLineRef} position={[0, -(artwork.height / 2 + 0.23), 0.030]} renderOrder={6}>
          <planeGeometry args={[borderW, 0.018]} />
          <meshBasicMaterial color="#c9a96e" transparent opacity={0.7} depthWrite={false} />
        </mesh>

        {/* ── Texte "Toucher / Cliquer pour découvrir" — ancré sous la ligne or ── */}
        {isNear && (
          <Html
            center
            position={[0, -(artwork.height / 2 + 0.50), 0.04]}
            style={{ pointerEvents: 'none', userSelect: 'none' }}
            zIndexRange={[400, 410]}
          >
            <span className="artwork-discover-frame-indicator">
              {isMobile ? 'TOUCHER POUR DÉCOUVRIR' : 'CLIQUER POUR DÉCOUVRIR'}
            </span>
          </Html>
        )}
      </group>
      <pointLight ref={primaryLightRef} position={[lightX, ROOM_H - 0.15, artwork.z]} color={lightColor} intensity={80} distance={9.5} decay={2} />
      {/* Fill light : desktop → full render ; mobile → distance réduite pour qualité sans surcharge */}
      <pointLight ref={fillLightRef} position={[isLeft ? -1.5 : 1.5, 5.5, artwork.z]} color="#F0E8D8"
        intensity={isMobile ? 6 : 10} distance={isMobile ? 4.5 : 6.5} decay={2} />
      {/* Cône de spot — desktop uniquement, visuellement trop lourd sur mobile */}
      {!isMobile && (
        <mesh position={[lightX, 7.45, artwork.z]}>
          <coneGeometry args={[0.78, 4.8, 12, 1, true]} />
          <meshBasicMaterial color={coneColor} transparent opacity={0.026} side={THREE.BackSide} depthWrite={false} />
        </mesh>
      )}
    </group>
  );
}
